import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  sendGetClientRequest,
  sendPatchClientRequest,
} from "../../../api/helper";
import { sendApiClientRequestExcel } from "../../../api/api";
import { serialize } from "../../../utils";

const initialState = {
  rows: [],
  total: 0,
  page: 1,
  limit: 10,
  loading: false,
  error: null,
  filter: {
    search: "",
    status: "",
    sort_dir: "desc", // 申請時間排序：desc=由新到舊（預設）/ asc=由舊到新
  },
  detail: null,
  detailLoading: false,
  //方案（inquiry-plans → data.packages）：用來把單據的 plan_ids 對應成方案名稱
  planPackages: [],
};

const buildQuery = ({ filter = {}, page, limit }) => {
  const q = {};
  if (filter.search) q.search = filter.search;
  if (filter.status) q.status = filter.status;
  if (filter.sort_dir) q.sort_dir = filter.sort_dir;
  if (filter.date_from) q.date_from = filter.date_from;
  if (filter.date_to) q.date_to = filter.date_to;
  if (page) q.page = page;
  if (limit) q.limit = limit;
  return Object.keys(q).length ? `?${serialize(q)}` : "";
};

// 詢問單所有 API 都回傳統一信封：{ error, msg, data }
const unwrapOrThrow = (response) => {
  if (!response || response.error) {
    const err = new Error(response?.msg || "API error");
    err.payload = response?.data;
    throw err;
  }
  return response.data;
};

// GET /api/inquiry-forms → data: { rows, total, limit, page }
export const fetchInquiryForms = createAsyncThunk(
  "inquiry/fetchForms",
  async ({ filter, page, limit } = {}) => {
    const qs = buildQuery({ filter, page, limit });
    const response = await sendGetClientRequest(`/api/inquiry-forms${qs}`);
    return unwrapOrThrow(response) ?? { rows: [], total: 0 };
  }
);

// GET /api/inquiry-forms/{id} → data: { ...form }
export const fetchInquiryFormDetail = createAsyncThunk(
  "inquiry/fetchDetail",
  async (inquiryId) => {
    const response = await sendGetClientRequest(
      `/api/inquiry-forms/${inquiryId}`
    );
    return unwrapOrThrow(response);
  }
);

// PATCH /api/inquiry-forms/{id}/status → data: { ...form }
// status: 'undo' | 'todo' | 'complete'
export const updateInquiryFormStatus = createAsyncThunk(
  "inquiry/updateStatus",
  async ({ id, status }) => {
    const response = await sendPatchClientRequest(
      `/api/inquiry-forms/${id}/status`,
      { status }
    );
    return unwrapOrThrow(response);
  }
);

// GET /api/inquiry-forms/export → 直接下載 xlsx（依 status / 申請時間區間過濾）
export const exportInquiryForms = createAsyncThunk(
  "inquiry/export",
  async ({ filter } = {}) => {
    const qs = buildQuery({ filter });
    const pad = (n) => String(n).padStart(2, "0");
    const d = new Date();
    const stamp = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(
      d.getDate()
    )}_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
    await sendApiClientRequestExcel(
      "get",
      `/api/inquiry-forms/export${qs}`,
      null,
      false,
      "blob",
      `inquiry_forms_${stamp}`
    );
  }
);

// GET /api/inquiry-plans → data: { rows, total, packages }
// 只取 packages（方案層，每筆帶自己的 plan_ids）；data.rows 是方案底下的價格檔位，中控不需要。
// 帶 with_trashed=1 連已停用／已刪除的方案一起取回（selectable=false），
// 歷史諮詢單引用的下架方案才對得到名稱；顯示端再自行過濾不可選且未被選的方案。
export const fetchInquiryPlans = createAsyncThunk(
  "inquiry/fetchPlans",
  async () => {
    const response = await sendGetClientRequest(
      `/api/inquiry-plans?with_trashed=1`
    );
    const data = unwrapOrThrow(response);
    return Array.isArray(data?.packages) ? data.packages : [];
  }
);

// 更新後把最新的 form 同步回列表與 detail，避免多打一次列表 API
const syncFormEverywhere = (state, form) => {
  if (!form || form.id == null) return;
  state.rows = state.rows.map((row) =>
    row.id === form.id ? { ...row, ...form } : row
  );
  if (state.detail && state.detail.id === form.id) {
    state.detail = { ...state.detail, ...form };
  }
};

const inquirySlice = createSlice({
  name: "inquiry",
  initialState,
  reducers: {
    setFilter: (state, { payload }) => {
      state.filter = { ...state.filter, ...payload };
      state.page = 1;
    },
    setPage: (state, { payload }) => {
      state.page = payload;
    },
    resetDetail: (state) => {
      state.detail = null;
    },
  },
  extraReducers: {
    [fetchInquiryForms.pending]: (state) => {
      state.loading = true;
      state.error = null;
    },
    [fetchInquiryForms.fulfilled]: (state, { payload }) => {
      state.loading = false;
      state.rows = Array.isArray(payload?.rows) ? payload.rows : [];
      state.total = Number.isFinite(payload?.total) ? payload.total : 0;
      if (Number.isInteger(payload?.page) && payload.page > 0) {
        state.page = payload.page;
      }
      if (Number.isInteger(payload?.limit) && payload.limit > 0) {
        state.limit = payload.limit;
      }
    },
    [fetchInquiryForms.rejected]: (state, { error }) => {
      state.loading = false;
      state.error = error?.message ?? "fetch failed";
    },

    [fetchInquiryFormDetail.pending]: (state) => {
      state.detailLoading = true;
    },
    [fetchInquiryFormDetail.fulfilled]: (state, { payload }) => {
      state.detailLoading = false;
      state.detail = payload;
    },
    [fetchInquiryFormDetail.rejected]: (state) => {
      state.detailLoading = false;
    },

    [updateInquiryFormStatus.fulfilled]: (state, { payload }) => {
      syncFormEverywhere(state, payload);
    },

    [fetchInquiryPlans.fulfilled]: (state, { payload }) => {
      state.planPackages = Array.isArray(payload) ? payload : [];
    },
    [fetchInquiryPlans.rejected]: (state) => {
      state.planPackages = [];
    },
  },
});

export const { setFilter, setPage, resetDetail } = inquirySlice.actions;
export const selectInquiry = (state) => state.inquiry;
export default inquirySlice.reducer;
